import { useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/ui/footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { hasPaidSubscription } from "@/lib/subscriptions";
import {
  BookOpen,
  User,
  Mail,
  Bell,
  Shield,
  Trash2,
  Save,
  Loader2,
  CheckCircle2,
  CreditCard,
  ExternalLink,
} from "lucide-react";

const STRIPE_PORTAL_URL = "https://billing.stripe.com/p/login/5kQ28q3FgbaTexdbcO38400";

export function SettingsPage() {
  const { user, loading, subscriptions } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Notification preferences
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [progressReminders, setProgressReminders] = useState(true);
  const [weeklyDigest, setWeeklyDigest] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    window.location.href = "/login";
    return null;
  }

  const handleSaveProfile = async () => {
    setSaving(true);
    // TODO: Implement profile update with Appwrite
    await new Promise((r) => setTimeout(r, 1000)); // Simulated delay
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      <main className="flex-1 max-w-3xl mx-auto w-full px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Settings</h1>
          <p className="text-muted-foreground">
            Manage your account settings and preferences.
          </p>
        </div>

        {/* Profile Section */}
        <section className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-lg">Profile</h2>
              <p className="text-sm text-muted-foreground">
                Update your personal information
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10"
                  disabled
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Contact support to change your email address.
              </p>
            </div>

            <Button onClick={handleSaveProfile} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : saved ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Saved!
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </section>

        {/* Notifications Section */}
        <section className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="font-semibold text-lg">Notifications</h2>
              <p className="text-sm text-muted-foreground">
                Manage how you receive updates
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="font-medium">Email Notifications</p>
                <p className="text-sm text-muted-foreground">
                  Receive important updates via email
                </p>
              </div>
              <input
                type="checkbox"
                checked={emailNotifications}
                onChange={(e) => setEmailNotifications(e.target.checked)}
                className="w-5 h-5 rounded border-border"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="font-medium">Practice Reminders</p>
                <p className="text-sm text-muted-foreground">
                  Get reminded to practice daily
                </p>
              </div>
              <input
                type="checkbox"
                checked={progressReminders}
                onChange={(e) => setProgressReminders(e.target.checked)}
                className="w-5 h-5 rounded border-border"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="font-medium">Weekly Progress Digest</p>
                <p className="text-sm text-muted-foreground">
                  Summary of your weekly progress
                </p>
              </div>
              <input
                type="checkbox"
                checked={weeklyDigest}
                onChange={(e) => setWeeklyDigest(e.target.checked)}
                className="w-5 h-5 rounded border-border"
              />
            </label>
          </div>
        </section>

        {/* Security Section */}
        <section className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Shield className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h2 className="font-semibold text-lg">Security</h2>
              <p className="text-sm text-muted-foreground">
                Manage your account security
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Password</p>
                <p className="text-sm text-muted-foreground">
                  Last changed: Never
                </p>
              </div>
              <Button variant="outline" size="sm">
                Change Password
              </Button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Email Verification</p>
                <p className="text-sm text-muted-foreground">
                  {user.emailVerification ? "Verified" : "Not verified"}
                </p>
              </div>
              {!user.emailVerification && (
                <Button variant="outline" size="sm">
                  Verify Email
                </Button>
              )}
            </div>
          </div>
        </section>

        {/* Subscription Section */}
        <section className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <h2 className="font-semibold text-lg">Subscription</h2>
              <p className="text-sm text-muted-foreground">Manage your billing and plan</p>
            </div>
          </div>

          {hasPaidSubscription(subscriptions) ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Active Plan</p>
                <p className="text-sm text-muted-foreground">
                  Cancel, update payment method, or view invoices via the Stripe portal.
                </p>
              </div>
              <a
                href={STRIPE_PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" size="sm" className="flex items-center gap-1.5">
                  Manage subscription <ExternalLink className="w-3.5 h-3.5" />
                </Button>
              </a>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Free Plan</p>
                <p className="text-sm text-muted-foreground">Upgrade to unlock unlimited practice and Aira AI.</p>
              </div>
              <a href="/paywall">
                <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white border-0">
                  Upgrade
                </Button>
              </a>
            </div>
          )}
        </section>

        {/* Danger Zone */}
        <section className="bg-card border border-destructive/50 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <h2 className="font-semibold text-lg text-destructive">
                Danger Zone
              </h2>
              <p className="text-sm text-muted-foreground">
                Irreversible actions
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Delete Account</p>
              <p className="text-sm text-muted-foreground">
                Permanently delete your account and all data
              </p>
            </div>
            <Button variant="destructive" size="sm">
              Delete Account
            </Button>
          </div>
        </section>
      </main>

      <div className="mx-auto px-8 w-full max-w-5xl">
        <Footer
          logo={
            <div className="w-10 h-10 rounded-full flex items-center justify-center">
              <BookOpen className="size-5" />
            </div>
          }
          brandName="Scholar Sphere"
          socialLinks={[]}
          mainLinks={[
            { href: "/", label: "Home" },
            { href: "/subjects", label: "Question Banks" },
            { href: "/dashboard", label: "Dashboard" },
          ]}
          legalLinks={[
            { href: "/privacy", label: "Privacy" },
            { href: "/terms", label: "Terms" },
          ]}
          copyright={{
            text: `© ${new Date().getFullYear()} Scholar Sphere`,
            license: "All rights reserved",
          }}
        />
      </div>
    </div>
  );
}
